import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState, type KeyboardEvent } from "react"
import { ChevronDown, ChevronRight, Plus, X } from "lucide-react"
import { Loading, ErrorBox } from "@/components/portal/LoadingError"
import { ControllingReiter } from "@/pages/ControllingPage"
import "./controlling.css"

/**
 * Controlling → Annahmen (Paul 25.10.2026).
 *
 * Alles, was der Forecast NICHT aus den Buchungen wissen kann, an einem Ort:
 *
 *  - **Annahmen** je Kunde/Lieferant: „endet ab" (gekuendigt, Vertrag aus)
 *    oder „fester Betrag ab" (Vertrag, Preisaenderung, neuer Kunde).
 *  - **Einmalige Buchungen**: bleiben im IST, zaehlen aber nicht in die Basis
 *    des Forecasts (Einrichtungsgebuehr beim Steuerberater). Markiert wird im
 *    Kontenblatt an der Buchung selbst, hier steht die Liste.
 *
 * 🔑 Nichts davon braucht ein Deployment — es sind Daten. Aendern legt eine
 * neue Fassung an, die alte bleibt im Verlauf: was der Forecast an welchem Tag
 * angenommen hat, muss sich spaeter nachvollziehen lassen.
 */

type Annahme = {
  id: number; konto: string; lieferant_name: string; art: "endet" | "betrag"; ab_monat: string
  betrag: number | null; notiz: string; erstellt_von: string; erstellt_am: string | null
  geloescht_von: string | null; geloescht_am: string | null; ersetzt_durch: number | null
}
type Einmalig = {
  id: number; buchung_id: string; konto: string; notiz: string; erstellt_von: string
  erstellt_am: string | null; vorhanden: boolean; datum: string | null; text: string | null
  lieferant: string | null; betrag_soll: number | null
}
type KontoWahl = { konto: string; name: string; gruppe: string; art: "ertrag" | "aufwand"; bebucht: boolean }
type Reihe = { konto: string; konto_name: string; art: "ertrag" | "aufwand"; name: string }

const eur2 = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" })
const MON = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"]
const monat = (m: string) => `${MON[Number(m.slice(5)) - 1]} ${m.slice(0, 4)}`
const zeit = (iso: string | null) => iso ? new Date(iso).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—"

async function holen<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" })
  if (!r.ok) throw new Error(`HTTP ${r.status}`)
  return r.json()
}

async function senden(url: string, method: string, body?: unknown) {
  const r = await fetch(url, {
    method, credentials: "include",
    headers: body ? { "Content-Type": "application/json" } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  })
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).error || `HTTP ${r.status}`)
  return r.json()
}

function naechsterMonat(): string {
  const d = new Date()
  d.setDate(1)
  d.setMonth(d.getMonth() + 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
}

type Entwurf = {
  id?: number                 // gesetzt = aendern
  reihe: string               // "konto|name" oder "" = neu
  konto: string; name: string
  art: "endet" | "betrag"; ab: string; betrag: string; notiz: string
}

const LEER: Entwurf = { reihe: "", konto: "", name: "", art: "endet", ab: naechsterMonat(), betrag: "", notiz: "" }

// ── Planungsraster (Paul 25.10.2026) ─────────────────────────────────────────
//
// Jede Zeile ein Kunde/Lieferant, jede Spalte ein Monat. ALLES steht drin —
// auch was das System selbst annimmt —, und an jeder Zelle sieht man, woher
// der Wert kommt: IST (abgeschlossen, gesperrt) · von Hand · Regel · automatisch.
// Sobald ein Monat abgeschlossen ist, ersetzt das IST den Plan von selbst.

type MonatInfo = { monat: string; status: "ist" | "vorlaeufig" | "laufend" | "offen" }
type PlanZelle = null | { id: number; betrag: number; notiz: string; von: string }
type RasterZeile = {
  konto: string; konto_name: string; art: "ertrag" | "aufwand"; name: string; schluessel: string
  typ: "monatlich" | "einmalig"; typ_fest: "monatlich" | "einmalig" | null
  ist: (number | null)[]; fc: (number | null)[]
  methoden: (string | null)[]; plan: PlanZelle[]; fc_jahr: number; nachtrag: (number | null)[]
}
type Auswertung = {
  monate: MonatInfo[]
  gruppen: { art: "ertrag" | "aufwand"; konten: { konto: string; name: string; lieferanten: Omit<RasterZeile, "konto" | "konto_name" | "art">[] }[] }[]
}

const zahl0f = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 })
// Ohne „-0" bei Cent-Betraegen (−0,13 € Kreditprovision).
const zahl0 = { format: (v: number) => (Math.abs(v) < 0.5 ? "–" : zahl0f.format(v)) }

function herkunft(m: string | null): "ist" | "hand" | "regel" | "auto" {
  if (!m) return "ist"
  if (m.startsWith("von Hand")) return "hand"
  if (m.startsWith("Annahme")) return "regel"
  return "auto"
}

function Planungsraster({ jahr }: { jahr: number }) {
  const qc = useQueryClient()
  const q = useQuery<{ auswertung: Auswertung | null }>({
    queryKey: ["controlling", jahr], queryFn: () => holen(`/api/v1/controlling?jahr=${jahr}`),
  })
  const [edit, setEdit] = useState<{ zeile: string; i: number; wert: string; nachtrag: boolean } | null>(null)
  const [neu, setNeu] = useState<null | { name: string; konto: string; monat: string; betrag: string }>(null)
  const [neuLaufend, setNeuLaufend] = useState<null | { name: string; konto: string }>(null)
  // Typ einer Zeile festlegen oder neue laufende Position anlegen — aendert
  // KEINE Betraege: Planwerte und Regeln von Hand gelten weiter vorrangig.
  const typSetzen = useMutation({
    mutationFn: (b: { konto: string; lieferant_name: string; typ: "monatlich" | "einmalig" | null }) =>
      senden("/api/v1/controlling/reihe", "PUT", b),
    onSuccess: () => { setNeuLaufend(null); qc.invalidateQueries({ queryKey: ["controlling"] }) },
  })
  const konten = useQuery<{ konten: KontoWahl[] }>({
    queryKey: ["controlling-konten"], queryFn: () => holen("/api/v1/controlling/konten"),
  })
  const setzen = useMutation({
    mutationFn: (b: { konto: string; lieferant_name: string; monat: string; betrag: number | null; typ?: string; nachtrag?: boolean; notiz?: string }) =>
      senden("/api/v1/controlling/plan", "PUT", b),
    onSuccess: () => { setEdit(null); setNeu(null); qc.invalidateQueries({ queryKey: ["controlling"] }) },
  })

  const a = q.data?.auswertung
  const zeilen: RasterZeile[] = useMemo(() => {
    const aus: RasterZeile[] = []
    for (const g of a?.gruppen ?? [])
      for (const k of g.konten)
        for (const l of k.lieferanten)
          aus.push({ ...l, konto: k.konto, konto_name: k.name, art: g.art })
    return aus
  }, [a])
  if (q.isLoading) return <Loading />
  if (!a) return null

  const bloecke: { titel: string; hinweis: string; zeilen: RasterZeile[]; vz: number }[] = [
    { titel: "Erlöse", hinweis: "je Kunde", vz: 1, zeilen: zeilen.filter((z) => z.art === "ertrag") },
    { titel: "Laufende Kosten (monatlich)", hinweis: "wiederkehrend", vz: -1,
      zeilen: zeilen.filter((z) => z.art === "aufwand" && z.typ === "monatlich") },
    { titel: "Einmalige Kosten", hinweis: "Einzelfälle und geplante Einmalposten", vz: -1,
      zeilen: zeilen.filter((z) => z.art === "aufwand" && z.typ === "einmalig") },
  ]
  const wert = (z: RasterZeile, i: number) => (a.monate[i].status === "ist" ? z.ist[i] : z.fc[i]) ?? 0
  const speichern = (z: RasterZeile, i: number, roh: string, nachtrag = false) => {
    const t = roh.trim()
    const betrag = t === "" ? null : Number(t.replace(/\./g, "").replace(",", "."))
    if (betrag !== null && (Number.isNaN(betrag) || betrag < 0)) return
    setzen.mutate({ konto: z.konto, lieferant_name: z.name, monat: a.monate[i].monat, betrag, nachtrag,
      notiz: nachtrag ? "Nachtrag — nicht in BuchhaltungsButler gebucht" : "" })
  }
  const taste = (e: KeyboardEvent<HTMLInputElement>, z: RasterZeile, i: number) => {
    if (e.key === "Enter") speichern(z, i, e.currentTarget.value, edit?.nachtrag ?? false)
    if (e.key === "Escape") setEdit(null)
  }

  return (
    <div className="ctl-karte" style={{ marginBottom: 14 }}>
      <div className="ctl-karte-kopf">
        <h3>Planung {jahr} nach Monaten</h3>
        <div className="ctl-legende">
          <span><i className="ctl-herkunft ist" />IST (gebucht)</span>
          <span><i className="ctl-herkunft hand" />von Hand</span>
          <span><i className="ctl-herkunft regel" />Regel „ab Monat“</span>
          <span><i className="ctl-herkunft auto" />automatisch</span>
          <span><i className="ctl-herkunft nachtrag" />Nachtrag (nicht gebucht)</span>
        </div>
      </div>
      <p className="ctl-klein" style={{ marginBottom: 8 }}>
        Auf eine Zelle klicken, Betrag eingeben, Enter. Leer lassen und Enter = zurück zum automatischen Wert.
        Abgeschlossene Monate zeigen das IST aus der Buchhaltung. Was dort nicht gebucht ist (z. B. Lohn über den Steuerberater),
        lässt sich per Klick als Nachtrag ergänzen. Kommen Buchungen, ersetzen sie den Plan von selbst.
      </p>
      {setzen.error && <div className="ctl-banner fehler">{(setzen.error as Error).message}</div>}
      <div className="ctl-tabelle-huelle">
        <table className="ctl-tabelle ctl-raster">
          <thead><tr>
            <th>Position</th>
            {a.monate.map((m, i) => <th key={m.monat} className={m.status !== "ist" ? "ctl-fc" : ""}>{MON[i]}</th>)}
            <th className="trenner">Jahr</th>
          </tr></thead>
          <tbody>
            {bloecke.map((b) => [
              <tr key={b.titel} className="kennzahl"><td title={b.hinweis}>{b.titel} <span className="ctl-methode">{b.zeilen.length}</span></td>
                {a.monate.map((m, i) => <td key={m.monat}>{zahl0.format(b.zeilen.reduce((s, z) => s + wert(z, i), 0))}</td>)}
                <td className="trenner">{zahl0.format(b.zeilen.reduce((s, z) => s + z.fc_jahr, 0))}</td></tr>,
              ...b.zeilen.map((z) => {
                const id = `${z.konto}|${z.schluessel}`
                return (
                  <tr key={id} className="konto">
                    <td title={`${z.konto} ${z.konto_name}`}>
                      {z.name}<span className="ctl-methode">{z.konto}</span>
                      {z.art === "aufwand" && (
                        <button className="ctl-link ctl-typ-knopf" disabled={typSetzen.isPending}
                                title={z.typ === "monatlich" ? "Als einmalige Kosten einordnen" : "Als laufende Kosten einordnen — wird monatlich fortgeschrieben"}
                                onClick={() => typSetzen.mutate({ konto: z.konto, lieferant_name: z.name, typ: z.typ === "monatlich" ? "einmalig" : "monatlich" })}>
                          {z.typ === "monatlich" ? "→ einmalig" : "→ laufend"}
                        </button>
                      )}
                    </td>
                    {a.monate.map((m, i) => {
                      const h = herkunft(z.methoden[i])
                      const offen = edit && edit.zeile === id && edit.i === i
                      const gebucht = m.status !== "ist" && m.status !== "offen" ? z.ist[i] : null
                      const nt = h === "ist" ? z.nachtrag[i] : null
                      const titel = h === "ist"
                        ? (nt != null ? `IST: gebucht ${eur2.format((z.ist[i] ?? 0) - nt)} + Nachtrag ${eur2.format(nt)} (nicht in der Buchhaltung)` : "IST, gebucht — Klick: Nachtrag für Ungebuchtes")
                        : `${z.methoden[i]}${gebucht ? ` · bisher gebucht ${eur2.format(gebucht)}` : ""}${z.plan[i]?.notiz ? ` · ${z.plan[i]!.notiz}` : ""}`
                      const oeffnen = () => {
                        if (offen) return
                        if (h === "ist") {
                          // Abgeschlossen = Buchhaltung. Nur was dort NICHT steht, darf
                          // als Nachtrag dazu — sonst zaehlt es doppelt.
                          if (nt == null && !confirm(`${MON[i]} ist abgeschlossen und zeigt die Buchhaltung.\n\nNachtrag für einen Betrag eintragen, der NICHT in BuchhaltungsButler gebucht ist (z. B. Lohn über den Steuerberater)?`)) return
                          setEdit({ zeile: id, i, wert: nt != null ? String(nt).replace(".", ",") : "", nachtrag: true })
                          return
                        }
                        setEdit({ zeile: id, i, wert: z.plan[i] ? String(z.plan[i]!.betrag).replace(".", ",") : "", nachtrag: false })
                      }
                      return (
                        <td key={m.monat} className={`zelle ${h}${nt != null ? " nachtrag" : ""}`} title={titel} onClick={oeffnen}>
                          {offen ? (
                            <input autoFocus defaultValue={edit!.wert} placeholder={zahl0.format(z.fc[i] ?? 0)}
                                   onKeyDown={(e) => taste(e, z, i)} onBlur={() => setEdit(null)} />
                          ) : (
                            <>
                              {Math.abs(wert(z, i)) < 0.5 ? "–" : zahl0.format(wert(z, i))}
                              {z.plan[i] && m.status !== "ist" && (
                                <button className="ctl-zelle-weg" title="Planwert entfernen — zurück zum automatischen Wert"
                                        onClick={(e) => { e.stopPropagation(); speichern(z, i, "") }}><X size={10} /></button>
                              )}
                              {nt != null && (
                                <button className="ctl-zelle-weg" title="Nachtrag entfernen"
                                        onClick={(e) => { e.stopPropagation(); speichern(z, i, "", true) }}><X size={10} /></button>
                              )}
                            </>
                          )}
                        </td>
                      )
                    })}
                    <td className="trenner" style={{ fontWeight: 600 }}>{zahl0.format(z.fc_jahr)}</td>
                  </tr>
                )
              }),
            ])}
            <tr className="kennzahl haupt"><td>Erlöse − Kosten</td>
              {a.monate.map((m, i) => {
                const v = bloecke.reduce((s, b) => s + b.vz * b.zeilen.reduce((t, z) => t + wert(z, i), 0), 0)
                return <td key={m.monat} className={v < 0 ? "ctl-neg" : ""}>{zahl0.format(v)}</td>
              })}
              <td className="trenner">{zahl0.format(bloecke.reduce((s, b) => s + b.vz * b.zeilen.reduce((t, z) => t + z.fc_jahr, 0), 0))}</td></tr>
          </tbody>
        </table>
      </div>
      {typSetzen.error && <div className="ctl-banner fehler" style={{ marginTop: 8 }}>{(typSetzen.error as Error).message}</div>}
      {!neu && !neuLaufend && (
        <div style={{ display: "flex", gap: 16, marginTop: 8, flexWrap: "wrap" }}>
          <button className="ctl-link" onClick={() => setNeuLaufend({ name: "", konto: "" })}>
            + Laufende Position anlegen (z. B. Personal, neuer Vertrag)
          </button>
          <button className="ctl-link" onClick={() => setNeu({ name: "", konto: "", monat: naechsterMonat(), betrag: "" })}>
            + Einmaligen Posten planen (z. B. Jahresabschluss, Anschaffung)
          </button>
        </div>
      )}
      {neuLaufend && (
        <div className="ctl-annahme-form" style={{ marginTop: 10 }}>
          <div className="ctl-klein" style={{ fontWeight: 600 }}>Laufende Position anlegen — die Beträge trägst du danach im Raster ein</div>
          <div className="ctl-formzeile">
            <label className="ctl-feld">Bezeichnung
              <input value={neuLaufend.name} placeholder="z. B. Gehalt Marlene" onChange={(e) => setNeuLaufend({ ...neuLaufend, name: e.target.value })} /></label>
            <label className="ctl-feld">Konto
              <select value={neuLaufend.konto} onChange={(e) => setNeuLaufend({ ...neuLaufend, konto: e.target.value })}>
                <option value="">— Konto wählen —</option>
                {[true, false].map((bb) => (
                  <optgroup key={String(bb)} label={bb ? "Bisher bebuchte Konten" : "Alle weiteren Erfolgskonten"}>
                    {(konten.data?.konten ?? []).filter((k) => k.bebucht === bb).map((k) => (
                      <option key={k.konto} value={k.konto}>{k.konto} {k.name}</option>))}
                  </optgroup>))}
              </select></label>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="ctl-knopf" disabled={!neuLaufend.name.trim() || !neuLaufend.konto || typSetzen.isPending}
                    onClick={() => typSetzen.mutate({ konto: neuLaufend.konto, lieferant_name: neuLaufend.name.trim(), typ: "monatlich" })}>Anlegen</button>
            <button className="ctl-knopf" onClick={() => setNeuLaufend(null)}>Abbrechen</button>
          </div>
        </div>
      )}
      {!neu ? null : (
        <div className="ctl-annahme-form" style={{ marginTop: 10 }}>
          <div className="ctl-klein" style={{ fontWeight: 600 }}>Einmaligen Posten planen</div>
          <div className="ctl-formzeile">
            <label className="ctl-feld">Bezeichnung / Lieferant
              <input value={neu.name} placeholder="z. B. Jahresabschluss Zimmermann-Brase" onChange={(e) => setNeu({ ...neu, name: e.target.value })} /></label>
            <label className="ctl-feld">Konto
              <select value={neu.konto} onChange={(e) => setNeu({ ...neu, konto: e.target.value })}>
                <option value="">— Konto wählen —</option>
                {[true, false].map((bb) => (
                  <optgroup key={String(bb)} label={bb ? "Bisher bebuchte Konten" : "Alle weiteren Erfolgskonten"}>
                    {(konten.data?.konten ?? []).filter((k) => k.bebucht === bb).map((k) => (
                      <option key={k.konto} value={k.konto}>{k.konto} {k.name}</option>))}
                  </optgroup>))}
              </select></label>
            <label className="ctl-feld">Monat
              <input type="month" value={neu.monat} onChange={(e) => setNeu({ ...neu, monat: e.target.value })} /></label>
            <label className="ctl-feld">Betrag € netto
              <input inputMode="decimal" value={neu.betrag} style={{ width: 110 }} onChange={(e) => setNeu({ ...neu, betrag: e.target.value })} /></label>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="ctl-knopf" disabled={!neu.name.trim() || !neu.konto || !neu.betrag.trim() || setzen.isPending}
                    onClick={() => setzen.mutate({ konto: neu.konto, lieferant_name: neu.name.trim(), monat: neu.monat,
                      betrag: Number(neu.betrag.replace(/\./g, "").replace(",", ".")), typ: "einmalig" })}>Speichern</button>
            <button className="ctl-knopf" onClick={() => setNeu(null)}>Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  )
}

export default function ControllingAnnahmenPage() {
  const qc = useQueryClient()
  const annahmen = useQuery<{ aktiv: Annahme[]; verlauf: Annahme[] }>({
    queryKey: ["controlling-annahmen"], queryFn: () => holen("/api/v1/controlling/annahmen"),
  })
  const einmalig = useQuery<{ buchungen: Einmalig[] }>({
    queryKey: ["controlling-einmalig"], queryFn: () => holen("/api/v1/controlling/einmalig"),
  })
  const konten = useQuery<{ konten: KontoWahl[] }>({
    queryKey: ["controlling-konten"], queryFn: () => holen("/api/v1/controlling/konten"),
  })
  // Die bekannten Kunden/Lieferanten kommen aus der Auswertung — dieselbe
  // Abfrage wie auf der Uebersicht, also meist schon im Cache.
  const jahr = new Date().getFullYear()
  const uebersicht = useQuery<{ auswertung: null | { gruppen: { art: "ertrag" | "aufwand"; konten: { konto: string; name: string; lieferanten: { name: string }[] }[] }[] } }>({
    queryKey: ["controlling", jahr], queryFn: () => holen(`/api/v1/controlling?jahr=${jahr}`),
  })
  const reihen: Reihe[] = useMemo(() => {
    const aus: Reihe[] = []
    for (const g of uebersicht.data?.auswertung?.gruppen ?? [])
      for (const k of g.konten)
        for (const l of k.lieferanten)
          if (l.name !== "ohne Beleg") aus.push({ konto: k.konto, konto_name: k.name, art: g.art, name: l.name })
    return aus.sort((a, b) => (a.art === b.art ? a.name.localeCompare(b.name, "de") : a.art === "ertrag" ? -1 : 1))
  }, [uebersicht.data])

  const [entwurf, setEntwurf] = useState<Entwurf | null>(null)
  const [verlaufOffen, setVerlaufOffen] = useState(false)
  const kontoName = (nr: string) => konten.data?.konten.find((k) => k.konto === nr)?.name ?? ""

  const neuLaden = () => {
    qc.invalidateQueries({ queryKey: ["controlling-annahmen"] })
    qc.invalidateQueries({ queryKey: ["controlling-einmalig"] })
    qc.invalidateQueries({ queryKey: ["controlling"] })
  }
  const speichern = useMutation({
    mutationFn: async (e: Entwurf) => {
      const body = {
        konto: e.konto, lieferant_name: e.name.trim(), art: e.art, ab_monat: e.ab,
        betrag: e.art === "betrag" ? Number(e.betrag.replace(/\./g, "").replace(",", ".")) : null,
        notiz: e.notiz,
      }
      return e.id ? senden(`/api/v1/controlling/annahmen/${e.id}`, "PUT", body)
                  : senden("/api/v1/controlling/annahmen", "POST", body)
    },
    onSuccess: () => { setEntwurf(null); neuLaden() },
  })
  const zuruecknehmen = useMutation({
    mutationFn: (id: number) => senden(`/api/v1/controlling/annahmen/${id}`, "DELETE"),
    onSuccess: neuLaden,
  })
  const einmaligWeg = useMutation({
    mutationFn: (id: number) => senden(`/api/v1/controlling/einmalig/${id}`, "DELETE"),
    onSuccess: neuLaden,
  })

  const aendern = (a: Annahme) => setEntwurf({
    id: a.id, reihe: `${a.konto}|${a.lieferant_name}`, konto: a.konto, name: a.lieferant_name, art: a.art,
    ab: a.ab_monat, betrag: a.betrag === null ? "" : String(a.betrag).replace(".", ","), notiz: a.notiz,
  })

  const gueltig = entwurf && entwurf.konto && entwurf.name.trim() && /^\d{4}-\d{2}$/.test(entwurf.ab)
    && (entwurf.art === "endet" || entwurf.betrag.trim())

  return (
    <div className="ctl" style={{ padding: "20px 24px" }}>
      <div className="ctl-kopf">
        <div>
          <h1 className="ctl-titel">Controlling</h1>
          <div className="ctl-unter">Planung nach Monaten — alles, was der Forecast annimmt, und wo es herkommt</div>
        </div>
        <ControllingReiter />
      </div>

      <Planungsraster jahr={jahr} />

      {(annahmen.isLoading || einmalig.isLoading) && <Loading />}
      {annahmen.error && <ErrorBox error={annahmen.error} />}

      <div className="ctl-karte" style={{ marginBottom: 14 }}>
        <div className="ctl-karte-kopf">
          <h3>Regeln „ab Monat …“ ({annahmen.data?.aktiv.length ?? 0})</h3>
          {!entwurf && (
            <button className="ctl-knopf" onClick={() => setEntwurf({ ...LEER, ab: naechsterMonat() })}>
              <Plus size={14} /> Neue Annahme
            </button>
          )}
        </div>

        {entwurf && (
          <div className="ctl-annahme-form" style={{ marginBottom: 12 }}>
            <div className="ctl-klein" style={{ fontWeight: 600 }}>{entwurf.id ? "Annahme ändern" : "Neue Annahme"}</div>
            <div className="ctl-formzeile">
              <label className="ctl-feld">Kunde / Lieferant
                <select value={entwurf.reihe} disabled={!!entwurf.id} onChange={(e) => {
                  const v = e.target.value
                  if (!v) return setEntwurf({ ...entwurf, reihe: "", konto: "", name: "" })
                  const [konto, ...rest] = v.split("|")
                  setEntwurf({ ...entwurf, reihe: v, konto, name: rest.join("|") })
                }}>
                  <option value="">— neuer Kunde / Lieferant —</option>
                  <optgroup label="Kunden (Erlöse)">
                    {reihen.filter((r) => r.art === "ertrag").map((r) => (
                      <option key={`${r.konto}|${r.name}`} value={`${r.konto}|${r.name}`}>{r.name} · {r.konto}</option>))}
                  </optgroup>
                  <optgroup label="Lieferanten (Kosten)">
                    {reihen.filter((r) => r.art === "aufwand").map((r) => (
                      <option key={`${r.konto}|${r.name}`} value={`${r.konto}|${r.name}`}>{r.name} · {r.konto} {r.konto_name}</option>))}
                  </optgroup>
                </select>
              </label>
              {!entwurf.reihe && (
                <>
                  <label className="ctl-feld">Name
                    <input value={entwurf.name} placeholder="z. B. Neukunde GmbH" onChange={(e) => setEntwurf({ ...entwurf, name: e.target.value })} />
                  </label>
                  <label className="ctl-feld">Konto
                    <select value={entwurf.konto} onChange={(e) => setEntwurf({ ...entwurf, konto: e.target.value })}>
                      <option value="">— Konto wählen —</option>
                      {[true, false].map((b) => (
                        <optgroup key={String(b)} label={b ? "Bisher bebuchte Konten" : "Alle weiteren Erfolgskonten"}>
                          {(konten.data?.konten ?? []).filter((k) => k.bebucht === b).map((k) => (
                            <option key={k.konto} value={k.konto}>{k.konto} {k.name} ({k.gruppe})</option>))}
                        </optgroup>
                      ))}
                    </select>
                  </label>
                </>
              )}
            </div>
            <div className="ctl-formzeile">
              <label className="ctl-feld">Art
                <select value={entwurf.art} onChange={(e) => setEntwurf({ ...entwurf, art: e.target.value as "endet" | "betrag" })}>
                  <option value="endet">endet (ab dann nichts mehr)</option>
                  <option value="betrag">fester Betrag je Monat, netto</option>
                </select>
              </label>
              <label className="ctl-feld">ab Monat
                <input type="month" value={entwurf.ab} onChange={(e) => setEntwurf({ ...entwurf, ab: e.target.value })} />
              </label>
              {entwurf.art === "betrag" && (
                <label className="ctl-feld">Betrag €
                  <input inputMode="decimal" value={entwurf.betrag} placeholder="1595" style={{ width: 120 }}
                         onChange={(e) => setEntwurf({ ...entwurf, betrag: e.target.value })} />
                </label>
              )}
            </div>
            <label className="ctl-feld">Notiz
              <input value={entwurf.notiz} placeholder="z. B. Vertrag endete im September" onChange={(e) => setEntwurf({ ...entwurf, notiz: e.target.value })} />
            </label>
            {speichern.error && <div className="ctl-klein" style={{ color: "var(--ctl-schlecht)" }}>{(speichern.error as Error).message}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button className="ctl-knopf" disabled={!gueltig || speichern.isPending} onClick={() => speichern.mutate(entwurf)}>Speichern</button>
              <button className="ctl-knopf" onClick={() => setEntwurf(null)}>Abbrechen</button>
            </div>
          </div>
        )}

        {annahmen.data && annahmen.data.aktiv.length === 0 && !entwurf && (
          <p className="ctl-klein">Noch keine Annahmen. Der Forecast rechnet alles aus den Buchungen fort.</p>
        )}
        {annahmen.data && annahmen.data.aktiv.length > 0 && (
          <div style={{ overflowX: "auto" }}>
            <table className="ctl-blatt">
              <thead><tr><th>Kunde / Lieferant</th><th>Konto</th><th>Annahme</th><th>ab</th><th>Notiz</th><th>angelegt</th><th /></tr></thead>
              <tbody>{annahmen.data.aktiv.map((a) => (
                <tr key={a.id}>
                  <td style={{ fontWeight: 600 }}>{a.lieferant_name}</td>
                  <td className="ctl-klein">{a.konto} {kontoName(a.konto)}</td>
                  <td>{a.art === "endet" ? "endet" : `${eur2.format(a.betrag ?? 0)} / Monat`}</td>
                  <td>{monat(a.ab_monat)}</td>
                  <td className="ctl-klein">{a.notiz}</td>
                  <td className="ctl-klein">{a.erstellt_von} · {zeit(a.erstellt_am)}</td>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <button className="ctl-link" onClick={() => aendern(a)}>ändern</button>{" · "}
                    <button className="ctl-link" onClick={() => { if (confirm(`Annahme für ${a.lieferant_name} zurücknehmen?`)) zuruecknehmen.mutate(a.id) }}>zurücknehmen</button>
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>

      <div className="ctl-karte" style={{ marginBottom: 14 }}>
        <div className="ctl-karte-kopf">
          <h3>Einmalige Buchungen ({einmalig.data?.buchungen.length ?? 0})</h3>
          <span className="ctl-klein">Bleiben im IST, zählen nicht in den Forecast</span>
        </div>
        <p className="ctl-klein" style={{ marginBottom: 8 }}>
          Markieren: Übersicht → Gruppe aufklappen → Konto anklicken → im Kontenblatt bei der Buchung auf „einmalig“.
          Zum Beispiel eine Einrichtungsgebühr: Der Monat zeigt sie weiter, der Forecast rechnet nur mit den laufenden Rechnungen.
        </p>
        {einmalig.data && einmalig.data.buchungen.length > 0 && (
          <table className="ctl-blatt">
            <thead><tr><th>Datum</th><th>Lieferant / Buchungstext</th><th>Konto</th><th className="zahl">Betrag</th><th>Notiz</th><th>markiert</th><th /></tr></thead>
            <tbody>{einmalig.data.buchungen.map((e) => (
              <tr key={e.id}>
                <td>{e.datum ? new Date(e.datum).toLocaleDateString("de-DE") : "—"}</td>
                <td>{e.vorhanden ? (e.lieferant || e.text) : <span style={{ color: "var(--ctl-schlecht)" }}>Buchung {e.buchung_id} gibt es nicht mehr (storniert/umgebucht)</span>}</td>
                <td className="ctl-klein">{e.konto} {kontoName(e.konto)}</td>
                <td className="zahl">{e.betrag_soll === null ? "—" : eur2.format(Math.abs(e.betrag_soll))}</td>
                <td className="ctl-klein">{e.notiz}</td>
                <td className="ctl-klein">{e.erstellt_von} · {zeit(e.erstellt_am)}</td>
                <td><button className="ctl-link" onClick={() => einmaligWeg.mutate(e.id)}>zurücknehmen</button></td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>

      <div className="ctl-karte">
        <button className="ctl-link" style={{ fontSize: 13, display: "inline-flex", alignItems: "center", gap: 4 }} onClick={() => setVerlaufOffen((x) => !x)}>
          {verlaufOffen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          Verlauf: zurückgenommene und geänderte Annahmen ({annahmen.data?.verlauf.length ?? 0})
        </button>
        {verlaufOffen && annahmen.data && (
          <table className="ctl-blatt" style={{ marginTop: 8 }}>
            <thead><tr><th>Kunde / Lieferant</th><th>Annahme</th><th>ab</th><th>Notiz</th><th>angelegt</th><th>beendet</th></tr></thead>
            <tbody>{annahmen.data.verlauf.map((a) => (
              <tr key={a.id}>
                <td>{a.lieferant_name} <span className="ctl-klein">{a.konto}</span></td>
                <td>{a.art === "endet" ? "endet" : `${eur2.format(a.betrag ?? 0)} / Monat`}</td>
                <td>{monat(a.ab_monat)}</td>
                <td className="ctl-klein">{a.notiz}</td>
                <td className="ctl-klein">{a.erstellt_von} · {zeit(a.erstellt_am)}</td>
                <td className="ctl-klein">{a.ersetzt_durch ? "geändert" : "zurückgenommen"} · {a.geloescht_von} · {zeit(a.geloescht_am)}</td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  )
}
